import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { venueMapsUrl } from "@/lib/venueMaps";
import axios from "axios";
import { ArrowLeft, Share2, Calendar, MapPin, User, Users, ExternalLink, Plane, Coins } from "lucide-react";
import { IKO } from "@/design-system";
import { nairobiDate, NAIROBI_TZ, formatDateLine } from "@/lib/datetime";
import DocumentTitle from "@/components/DocumentTitle";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";
import { PosterViewer, ScrimHero, ScrimButton, useWidthBand,
         metaLine, ikoTextOnNight, CompactCard, VerticalCard, nextStartAt } from "./feedCard";
import useSafeBack from "@/hooks/useSafeBack";
import SaveLikeRow from "@/design-system/SaveLikeRow";
import EventComposers from "./EventComposers";
import { PassedCard, HowItWent, InternetDrawer, ExplainerDrawer, InsidersDrawer, UpcomingReviewsLock, CuratorInternetRow, SafariReviewsBox } from "./EventScore";
import { ChainBar, MoreInChain } from "@/components/member/ChainBits";
import SendToSquadButton from "./SendToSquadButton";
import { trackTicketClick } from "@/lib/analytics";
import { upperKES, CURRENCY_CODES, CURRENCY_SPLIT } from "@/lib/currency";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });
const TZ = "Africa/Nairobi";
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD_BG = "var(--ww-surface)";

function bearer() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * D46 Addendum 3 §C — Event page redesign.
 * · Scrim rule everywhere: blurred backdrop under night overlay #1A1A30 @ 90%.
 * · Sharp poster centred, iko-coloured 5-6px frame, 10px radius, ~150×200 (phone).
 * · No text over images. All content on the night ground below/beside poster.
 * · ≥600px: two-column layout, poster left sticky, details right, buttons in one row.
 * · Rows: date+time · venue (tap→page) · organiser (tap→page, hidden if empty) · going · CTAs · about
 * · Score section only when a published score exists.
 * · Below (≥600px): "more at this venue" + "more from this curator" (up to 3 each).
 */
export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // Msg 234 §4 · when the "your story" tile lands here with ?compose=photo,
  // the photo composer opens automatically and scrolls into view.
  const composeIntent = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get("compose");
  }, [location.search]);
  const { wide } = useWidthBand();
  const [ev, setEv] = useState(null);
  const [venue, setVenue] = useState(null);
  const [chain, setChain] = useState(null); // WW-173
  const [venueMore, setVenueMore] = useState([]);
  const [orgMore, setOrgMore] = useState([]);
  const [summary, setSummary] = useState({ interested: 0, going: 0, own: null });
  const [went, setWent] = useState(false);
  const [iWent, setIWent] = useState(false);   // Msg 234 · self-declared
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);
  const [insiders, setInsiders] = useState({ count: 0, average: null });
  const [sources, setSources] = useState({ event: [], curator: [] });
  const [drawer, setDrawer] = useState(null);
  const posterRef = useRef(null);
  // WW-205 · a card's "N going" lands on the going strip.
  useEffect(() => {
    if (!loading && location.hash === "#going") {
      document.getElementById("going")?.scrollIntoView({ block: "center" });
    }
  }, [loading, location.hash]);

  const isPaidMember = !!user && ["trialing", "founding", "paid", "comped"].includes(user.membership_state);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      client.get(`/public/events/${id}`),
      client.get(`/events/${id}/attendance/summary`, { headers: bearer() }).catch(() => ({ data: { interested: 0, going: 0, own: null } })),
      user ? client.get(`/events/${id}/attended/${user.user_id}`, { headers: bearer() }).catch(() => ({ data: { attended: false } })) : Promise.resolve({ data: { attended: false } }),
      user ? client.get(`/events/${id}/i-went`, { headers: bearer() }).catch(() => ({ data: { i_went: false } })) : Promise.resolve({ data: { i_went: false } }),
    ]).then(([a, b, c, d]) => {
      if (!alive) return;
      setEv(a.data.event); setVenue(a.data.venue || null); setSummary(b.data); setChain(a.data.chain || null);
      setInsiders(a.data.insiders || { count: 0, average: null });
      setSources({ event: a.data.internet_sources || [], curator: a.data.curator_internet_sources || [] });
      setWent(!!c.data.attended); setIWent(!!d.data.i_went);
    }).catch(() => toast.error("event not found")).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, user]);

  const onIWent = async () => {
    if (!user) return nav(`/admin/login?next=/app/event/${id}`);
    if (!isPaidMember) return nav("/app/membership");
    try {
      await client.post(`/events/${id}/i-went`, {}, { headers: bearer() });
      setIWent(true);
      toast.success("you're marked as went. leave a review or share a photo.");
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail.hint || detail.error || "could not mark") : (detail || "could not mark");
      toast.error(msg);
      return false;
    }
  };

  // WW-169 · "review this event" also marks that you went, then opens the composer.
  const openCompose = async (compose) => {
    if (!user) return nav(`/admin/login?next=/app/event/${id}`);
    if (!isPaidMember) return nav("/app/membership");
    if (!went && !iWent && !(await onIWent())) return;
    setTimeout(() => window.dispatchEvent(new CustomEvent("ww:composer:open", { detail: { compose } })), 250);
  };

  // Related listings for wide layout.
  useEffect(() => {
    if (!ev) return;
    if (venue?.id) client.get(`/public/venues/${venue.id}`).then((r) => setVenueMore((r.data.events || []).filter((e) => e.id !== ev.id).slice(0, 3))).catch(() => {});
    const h = (ev.organiser_handle || "").replace(/^@+/, "");
    if (h) client.get(`/public/organisers/${encodeURIComponent(h)}`).then((r) => setOrgMore((r.data.events || []).filter((e) => e.id !== ev.id).slice(0, 3))).catch(() => {});
  }, [ev, venue]);

  const setIntent = async (state) => {
    if (!user) return nav(`/admin/login?next=/app/event/${id}`);
    try {
      await client.put(`/events/${id}/attendance/intent`, { state }, { headers: bearer() });
      const r = await client.get(`/events/${id}/attendance/summary`, { headers: bearer() });
      setSummary(r.data);
      toast.success(state === "going" ? "you're going" : state === "interested" ? "marked interested" : "cleared");
      // Wave 16.2 (Vania 2 Oct) · going_tap / interested_tap fire on the
      // positive action only. Clearing ("none") is NOT a tap.
      if (state === "going" || state === "interested") {
        import("@/lib/analytics").then((m) => {
          if (state === "going") m.trackGoing(id);
          else m.trackInterested(id);
        });
      }
    } catch { toast.error("could not update"); }
  };

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: ev?.title || "watu wako", url });
      else { await navigator.clipboard.writeText(url); toast.success("link copied"); }
    } catch (err) {
      // User dismissed share sheet — copy as fallback (silent)
      try { await navigator.clipboard.writeText(url); toast.success("link copied"); } catch {}
    }
  };

  // Vania Msg 224 · reliable back — nav(-1) when we have SPA history,
  // otherwise (direct-link visits) fall through to For You.
  const onBack = useSafeBack("/app");

  if (loading) return <p className="p-8 text-center" style={{ color: META }}>loading…</p>;
  if (!ev) return <p className="p-8 text-center" style={{ color: META }}>event not found</p>;

  const iko = ikoTextOnNight(ev.iko);
  const ikoBright = IKO[ev.iko]?.bright || "#FFD60A";
  const price = (ev.price || "").trim();
  const priceMissing = !price || price.toLowerCase() === "price to confirm";
  const isPaid = price && !priceMissing && price.toLowerCase() !== "free";
  const cleanHandle = (ev.organiser_handle || "").replace(/^@+/, "");

  const heroH = wide ? 380 : 300;
  const posterW = wide ? 240 : 150;
  const posterH = wide ? 320 : 200;
  const openPoster = () => ev.cover_image_url && setViewer({ src: ev.cover_image_url, alt: ev.title || "", returnEl: posterRef.current });

  // WW-146.j2 · fully funded safari items say so on the ticket button.
  const ctaLabel = ev.safari?.cost === "fully_funded" && isSafari(ev) ? "fully funded" : priceMissing
    ? "price on the ticket page"
    : (isPaid ? `get tickets · ${upperKES(price)}` : "free · rsvp");
  const ctaTarget = ev.ticket_link || "#";
  const nowIso = new Date().toISOString();
  const ended = (ev.end_at || ev.start_at || "") < nowIso;
  const upcoming = (ev.start_at || "") > nowIso;
  // WW-146.j · safari (outside Kenya) items never show a score.
  const scorePart = isSafari(ev) ? <SafariReviewsBox /> : ended ? (
    <HowItWent score={ev.score} roses={ev.roses} insiders={insiders} sources={sources.event} onOpen={setDrawer} iko={ev.iko} />
  ) : upcoming ? (
    <>
      <UpcomingReviewsLock startAt={nextStartAt(ev) || ev.start_at} />
      {sources.curator.length > 0 && <CuratorInternetRow count={sources.curator.length} onOpen={() => setDrawer("curator")} />}
    </>
  ) : null;
  const composers = upcoming ? null : (
    <EventComposers eventId={ev.id} isPaid={isPaidMember}
                    ownIntent={summary.own} passed={ended} iko={ev.iko}
                    eventStarted={(ev.start_at || "") <= nowIso}
                    iWent={iWent} onIWent={onIWent}
                    initialOpen={composeIntent}
                    canPostAndReview={isPaidMember && (went || iWent)} />
  );
  const ctas = (
    <CTAButtons ev={ev} eventId={ev.id} isPaid={isPaid} ctaLabel={ctaLabel} ctaTarget={ctaTarget}
                organiserHandle={cleanHandle} setIntent={setIntent} summary={summary}
                eventEnded={ended} onCompose={openCompose} />
  );

  return (
    <div data-testid="event-detail" className="flex h-full flex-col"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title={ev.title ? `${ev.title.toLowerCase()} · watu wako` : "watu wako"} />

      <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
        {/* Scrim hero — Rule B */}
        <ScrimHero image={ev.cover_image_url} height={heroH} iko={ev.iko} testId="event-scrim">
          <ScrimButton ariaLabel="back" testId="event-back" onClick={onBack}
                       style={{ position: "absolute", left: 16, top: 16 }}>
            <ArrowLeft size={18} />
          </ScrimButton>
          {/* Wave C point L (updated by Q + P · Vania 7 Oct) · the top action
              row holds share + send-to-squad. Bookmark is gone (merged into
              the "interested" button below). "add to squad" was renamed and
              fixed as a "send to squad" drawer. 44px icon buttons. */}
          <div style={{ position: "absolute", right: 16, top: 16, display: "flex", gap: 8 }}>
            <div data-testid="event-top-send-to-squad-slot">
              <SendToSquadButton eventId={ev.id} testIdPrefix="event-top-" compact />
            </div>
            <ScrimButton ariaLabel="share" testId="event-share" onClick={onShare}>
              <Share2 size={18} />
            </ScrimButton>
          </div>
          {/* Vania Msg 224 · pointer-events:none so the wrapper doesn't
              swallow clicks meant for the back / share buttons behind it.
              The poster <button> itself re-enables pointer events. */}
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
            <span ref={posterRef} role="button" tabIndex={0}
                    onClick={openPoster}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPoster(e); } }}
                    aria-label="open poster"
                    data-testid="event-poster-open"
                    style={{ background: "transparent", padding: 0, border: 0, cursor: ev.cover_image_url ? "zoom-in" : "default", pointerEvents: "auto", display: "inline-block", maxWidth: "100%", position: "relative" }}>
              {ev.cover_image_url ? (
                <img src={ev.cover_image_url} alt={ev.title || ""}
                     style={{ width: posterW, height: posterH, maxWidth: "100%", objectFit: "contain",
                              border: `${wide ? 6 : 5}px solid ${ikoBright}`, borderRadius: 10,
                              boxShadow: "0 20px 40px rgba(0,0,0,0.45)", background: "#0f0f22" }} />
              ) : (
                // 12.19b (Vania 1 Oct) — readable fallback (white on near-black bg).
                <div style={{ width: posterW, height: posterH, maxWidth: "100%", border: `${wide ? 6 : 5}px solid ${ikoBright}`,
                              borderRadius: 10, display: "grid", placeItems: "center",
                              background: "#0f0f22", color: "#FBF4E9", fontFamily: "Poppins", fontWeight: 600 }}>
                  no poster
                </div>
              )}
              {ended && (
                <span aria-hidden="true" data-testid="event-this-happened-stamp"
                      style={{ position: "absolute", left: "50%", top: "50%", width: 154, padding: "6px 0",
                               transform: "translate(-50%, -50%) rotate(-12deg)", textAlign: "center",
                               fontFamily: "Poppins", fontWeight: 800, fontSize: 15, letterSpacing: ".12em",
                               textTransform: "uppercase", color: "#FFFFFF", border: "3px solid #FFFFFF",
                               borderRadius: 8, background: "rgba(26,26,48,.78)", pointerEvents: "none" }}>
                  this happened
                </span>
              )}
            </span>
          </div>
        </ScrimHero>

        <div className="ww-container pt-6 pb-10">
          {wide ? (
            <div className="grid gap-8" style={{ gridTemplateColumns: "1fr 1.4fr" }}>
              {/* Left column stays empty visually; poster is inside the hero. Sticky info card here. */}
              <aside>
                <div className="rounded-2xl border p-4 sticky" style={{ top: 24, borderColor: LINE, background: CARD_BG }}>
                  <ChainBar chain={chain} />
                  <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 11, color: `var(--iko-title-${ev.iko || "sherehe"})` }}>
                    {(IKO[ev.iko]?.label || "event").toLowerCase()}
                  </p>
                  <h1 className="mt-2 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.03em", fontSize: 40, lineHeight: 1.02 }}>
                    {(ev.title || "").toLowerCase()}
                  </h1>
                  <InfoRows ev={ev} venue={venue} cleanHandle={cleanHandle} dimDate={ended} />
                  <GoingRow summary={summary} ended={ended} />
                  {ctas}
                  {!ended && <TicketHint htgType={ev.how_to_get_in?.type} />}
                </div>
              </aside>
              <div>
                <AboutSection ev={ev} />
                <SlotsSection ev={ev} />
                {scorePart}
                <MoreInChain chain={chain} />
                <WhereBlock ev={ev} venue={venue} />
                <WhoBlock ev={ev} cleanHandle={cleanHandle} orgMore={orgMore} />
                {composers}
                <RelatedList title="more at this venue" rows={venueMore} setViewer={setViewer}
                             empty={venue ? `no other upcoming events at ${(venue.name || "").toLowerCase()} yet.` : null} />
                <RelatedList title="more from this curator" rows={orgMore} setViewer={setViewer}
                             empty={cleanHandle ? `no other upcoming events from @${cleanHandle} yet.` : null} />
              </div>
            </div>
          ) : (
            <>
              <ChainBar chain={chain} />
              <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 11, color: `var(--iko-title-${ev.iko || "sherehe"})` }}>
                {(IKO[ev.iko]?.label || "event").toLowerCase()}
              </p>
              <h1 className="mt-2 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.03em", fontSize: 28, lineHeight: 1.05 }}>
                {(ev.title || "").toLowerCase()}
              </h1>
              <InfoRows ev={ev} venue={venue} cleanHandle={cleanHandle} dimDate={ended} />
              <GoingRow summary={summary} ended={ended} />
              {ctas}
              {!ended && <TicketHint htgType={ev.how_to_get_in?.type} />}
              <AboutSection ev={ev} />
              <SlotsSection ev={ev} />
              {scorePart}
              <MoreInChain chain={chain} />
              <WhereBlock ev={ev} venue={venue} />
              <WhoBlock ev={ev} cleanHandle={cleanHandle} orgMore={orgMore} />
              {composers}
              {went && (
                <p className="mt-4 rounded-md border px-3 py-2 text-xs"
                   style={{ borderColor: "#FFD60A", color: "#FFD60A", fontFamily: "Poppins" }}>
                  ✓ verified attended — rating stays staff-only in phase one
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {viewer && <PosterViewer viewer={viewer} onClose={() => setViewer(null)} />}
      {drawer === "internet" && <InternetDrawer title="what the internet says" sources={sources.event} onClose={() => setDrawer(null)} />}
      {drawer === "curator" && <InternetDrawer title="what the internet says about this curator" sources={sources.curator} onClose={() => setDrawer(null)} />}
      {drawer === "explain" && <ExplainerDrawer score={ev.score} insiders={insiders} iko={ev.iko} onClose={() => setDrawer(null)} />}
      {drawer === "insiders" && <InsidersDrawer eventId={ev.id} iko={ev.iko} onClose={() => setDrawer(null)} />}
    </div>
  );
}

/* --- Sub-components --- */

function Tile({ icon: Icon, children, testId, to, onClick, ariaLabel }) {
  const inner = (
    <div className="flex items-center gap-3 py-3" style={{ minHeight: 44 }}>
      <span style={{
        width: 36, height: 36, borderRadius: 10,
        background: CARD_BG, border: `1px solid ${LINE}`,
        display: "grid", placeItems: "center", flexShrink: 0, color: "var(--ww-text)",
      }}><Icon size={16} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
  if (to) return <Link to={to} data-testid={testId} aria-label={ariaLabel}
                       style={{ display: "block", textDecoration: "none", color: "inherit" }}>{inner}</Link>;
  if (onClick) return <button onClick={onClick} data-testid={testId} aria-label={ariaLabel}
                              style={{ display: "block", background: "transparent", border: 0, textAlign: "left", width: "100%", color: "inherit", cursor: "pointer" }}>{inner}</button>;
  return <div data-testid={testId}>{inner}</div>;
}

const COUNTRY_NAMES = { UG: "uganda", TZ: "tanzania", RW: "rwanda", ET: "ethiopia", ZA: "south africa", NG: "nigeria",
  GH: "ghana", BE: "belgium", GB: "united kingdom", US: "united states", FR: "france", DE: "germany",
  NL: "netherlands", AE: "uae", IN: "india", TR: "turkey", EG: "egypt", CI: "côte d'ivoire" };
const COVER_WORDS = { flights: "flights", stay: "stay", meals: "meals", visa_letter: "visa letter" };
const isSafari = (ev) => (ev?.country || "KE").toUpperCase() !== "KE";
const listWords = (a) => (a.length < 2 ? a.join("") : `${a.slice(0, -1).join(", ")} and ${a[a.length - 1]}`);

function SafariRows({ ev }) {
  const s = ev.safari || {};
  const covers = (s.covers || []).map((c) => COVER_WORDS[c] || c);
  const price = (ev.price || "").trim();
  const cost = s.cost === "fully_funded"
    ? (covers.length ? `fully funded · the organisers pay ${listWords(covers)}` : "fully funded")
    : (price ? upperKES(price) : "");
  return (
    <>
      <Tile icon={Plane} testId="event-info-safari-trip">
        <p style={{ fontFamily: "Arial", fontSize: 14 }}>
          outside kenya · {COUNTRY_NAMES[(ev.country || "").toUpperCase()] || (ev.country || "").toLowerCase()} · plan a trip
        </p>
      </Tile>
      {cost && (
        <Tile icon={Coins} testId="event-info-safari-cost">
          <p style={{ fontFamily: "Arial", fontSize: 14 }}>{cost}</p>
        </Tile>
      )}
    </>
  );
}

function InfoRows({ ev, venue, cleanHandle, dimDate }) {
  const mapsUrl = venue ? venueMapsUrl(venue) : null;
  return (
    <div className="mt-5 divide-y divide-[color:var(--ww-line)]" style={{ borderColor: LINE }}>
      <Tile icon={Calendar} testId="event-info-date">
        <p data-testid="event-info-date-text" style={{ fontFamily: "Arial", fontSize: 14, color: dimDate ? META : "var(--ww-text)" }}>
          {formatDateLine(nextStartAt(ev) || ev.start_at, ev.next_slot?.end_at || ev.end_at)}
        </p>
        {ev.has_multiple_slots && (
          <p data-testid="event-more-slots-hint"
             style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 2 }}>
            <a href="#event-slots" style={{ color: "#FFD60A", textDecoration: "underline" }}>
              see all {(ev.slots_view || []).length} dates
            </a>
          </p>
        )}
      </Tile>
      {(venue?.id || ev.venue_display) && (
        <>
          <Tile icon={MapPin} to={venue?.id ? `/app/venue/${venue.id}` : undefined} testId="event-info-venue"
                ariaLabel="open venue page">
            <p style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, textTransform: "lowercase" }}>
              {(venue?.name || ev.venue_display || "").toLowerCase()}
            </p>
            {venue?.area && (
              <p style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 2 }}>
                {venue.area.toLowerCase()}
              </p>
            )}
          </Tile>
          {/* Wave C point M addition (Vania 7 Oct) · "open in maps" now has
              16px of breathing room above AND below (was 6px top with the
              divider touching it below). Divider is the next Tile's top
              border; our marginBottom: 16 enforces the spacing rule
              "min 16 between a link and a divider" on the event page. */}
          {mapsUrl && (
            <a href={mapsUrl} target="_blank" rel="noreferrer"
               data-testid="event-info-venue-maps"
               style={{ marginTop: 16, marginBottom: 16, alignSelf: "flex-start",
                        display: "inline-flex", alignItems: "center", gap: 6,
                        fontFamily: "Arial", fontSize: 12, color: "var(--ww-text)",
                        border: "1px solid var(--ww-accent-ink)", borderRadius: 999,
                        padding: "8px 14px", textDecoration: "none",
                        textTransform: "lowercase", minHeight: 36 }}>
              <MapPin size={12} /> open in maps
            </a>
          )}
        </>
      )}
      {/* WW-146.j1 · date, venue, outside kenya row, cost row, then by */}
      {isSafari(ev) && <SafariRows ev={ev} />}
      {cleanHandle && (
        <Tile icon={User} to={`/app/organiser/${cleanHandle}`} testId="event-info-organiser"
              ariaLabel="open curator page">
          <p style={{ fontFamily: "Arial", fontSize: 13, color: META }}>by</p>
          <OrganisersLine mainHandle={cleanHandle} coHandles={ev.co_organiser_handles || []} />
        </Tile>
      )}
    </div>
  );
}

function OrganisersLine({ mainHandle, coHandles }) {
  const [open, setOpen] = React.useState(false);
  const list = (coHandles || []).map((h) => (h || "").replace(/^@+/, "")).filter((h) => h && h !== mainHandle);
  if (list.length === 0) {
    return (
      <p style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "var(--ww-text)" }}>
        @{mainHandle}
      </p>
    );
  }
  if (list.length === 1) {
    return (
      <p style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "var(--ww-text)" }}>
        @{mainHandle} <span style={{ color: "var(--ww-text-muted)", fontWeight: 500 }}>and</span>{" "}
        <Link to={`/app/organiser/${list[0]}`} data-testid="event-co-organiser-0"
              onClick={(e) => e.stopPropagation()}
              style={{ color: "var(--ww-text)", textDecoration: "underline" }}>
          @{list[0]}
        </Link>
      </p>
    );
  }
  // 2+ co-organisers: show first + "and N more" popover
  return (
    <div data-testid="event-co-organisers">
      <p style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "var(--ww-text)" }}>
        @{mainHandle}
        <span style={{ color: "var(--ww-text-muted)", fontWeight: 500 }}>, </span>
        <Link to={`/app/organiser/${list[0]}`} data-testid="event-co-organiser-0"
              onClick={(e) => e.stopPropagation()}
              style={{ color: "var(--ww-text)", textDecoration: "underline" }}>
          @{list[0]}
        </Link>
        <span style={{ color: "var(--ww-text-muted)", fontWeight: 500 }}> and </span>
        <button type="button" data-testid="event-co-organisers-more"
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                style={{ background: "transparent", border: 0, padding: 0,
                         color: "var(--ww-text)", textDecoration: "underline",
                         fontFamily: "Poppins", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
          {list.length - 1} more
        </button>
      </p>
      {open && (
        <ul data-testid="event-co-organisers-list"
            style={{ marginTop: 6, padding: "8px 10px", borderRadius: 10,
                     background: "var(--ww-surface)", border: "1px solid var(--ww-line)",
                     listStyle: "none" }}>
          {list.slice(1).map((h) => (
            <li key={h} style={{ padding: "4px 0" }}>
              <Link to={`/app/organiser/${h}`}
                    data-testid={`event-co-organiser-link-${h}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14,
                             color: "var(--ww-text)", textDecoration: "underline" }}>
                @{h}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GoingRow({ summary, ended }) {
  const going = summary.going || 0;
  // WW-182 · an ended event never invites "be the first to go".
  if (ended && going === 0) return null;
  const label = going > 0 ? (going === 1 ? "1 going" : `${going} going`) : "be the first to go";
  const palette = ["#FFD60A", "#FF6B9D", "#9D4EDD"];
  const dots = Math.min(3, going);
  return (
    <div className="flex items-center gap-3 py-3" data-testid="event-going-strip" id="going"
         style={{ borderTop: `1px solid ${LINE}` }}>
      <span style={{ width: 36, height: 36, borderRadius: 10, background: CARD_BG,
                     border: `1px solid ${LINE}`, display: "grid", placeItems: "center", color: "var(--ww-text)" }}>
        <Users size={16} />
      </span>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
        {dots > 0 && Array.from({ length: dots }).map((_, i) => (
          <span key={i} style={{
            display: "inline-block", width: 22, height: 22, borderRadius: 999,
            background: palette[i % 3], border: "2px solid var(--ww-surface)",
            marginLeft: i === 0 ? 0 : -8,
          }} />
        ))}
        <span style={{ fontFamily: "Arial", fontSize: 13, color: going ? "var(--ww-text)" : META }}>{label}</span>
      </div>
    </div>
  );
}

function CTAButtons({ ev, eventId, isPaid, ctaLabel, ctaTarget, setIntent, summary, organiserHandle, eventEnded, onCompose }) {
  const goingActive = summary.own === "going";
  const intActive = summary.own === "interested";
  // Wave 10.1 (Vania 1 Oct · update) · render the "KES" inside a .ww-currency
  // span so the parent `textTransform: lowercase` never downcases it.
  const parts = (ctaLabel || "").split(CURRENCY_SPLIT);
  // Wave C.12 (Vania 7 Oct) · past events hide "get tickets" entirely. The
  // block becomes "this event has passed" + two CTAs for members who went:
  // "write a review" and "add a photo" (compose intent passed through the
  // existing composers). Non-paid members still see the hint; the composers
  // gate on `canPostAndReview`.
  if (eventEnded) {
    // WW-169 · one "this event has passed" card; review also marks that you went.
    return <PassedCard onReview={() => onCompose("review")} onPhoto={() => onCompose("photo")} />;
  }
  return (
    <div className="mt-4 grid gap-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
      <button data-testid="event-going"
              onClick={() => setIntent(goingActive ? "none" : "going")}
              style={{
                padding: "12px 16px", borderRadius: 12,
                background: goingActive ? "var(--ww-text)" : "transparent",
                color: goingActive ? "var(--ww-ground)" : "var(--ww-text)",
                border: `1px solid ${goingActive ? "var(--ww-text)" : "var(--ww-line-strong)"}`,
                fontFamily: "Poppins", fontWeight: 600, fontSize: 14, textTransform: "lowercase",
                minHeight: 44, cursor: "pointer",
              }}>
        i'm going
      </button>
      <button data-testid="event-interested"
              onClick={() => setIntent(intActive ? "none" : "interested")}
              style={{
                padding: "12px 16px", borderRadius: 12,
                background: intActive ? "var(--ww-text)" : "transparent",
                color: intActive ? "var(--ww-ground)" : "var(--ww-text)",
                border: `1px solid ${intActive ? "var(--ww-text)" : "var(--ww-line-strong)"}`,
                fontFamily: "Poppins", fontWeight: 600, fontSize: 14, textTransform: "lowercase",
                minHeight: 44, cursor: "pointer",
              }}>
        interested
      </button>
      {/* Wave C point L (Vania 7 Oct · supersedes bookmark + add to squad):
          the bottom row is just the two intent buttons + the ticket CTA.
          "interested" above is the merged save+interested action (bookmark
          icon · see Q). The top row carries share + send-to-squad. */}
      {/* WW-158 (Vania 13 Oct · D-4) · when the curator is contactable
          by phone / email instead of a ticket URL, swap the "get
          tickets" button for a Call + Email pair. "walk_in" shows a
          simple "just show up" pill. */}
      {(() => {
        const htg = ev?.how_to_get_in || null;
        if (htg && htg.type === "contact" && (htg.contact_phone || htg.contact_email)) {
          const name = (htg.contact_name || "").trim();
          const headline = name ? `to attend, contact ${name.toLowerCase()}` : "to attend, contact the curator";
          const buttons = [];
          if (htg.contact_phone) {
            const tel = `tel:${String(htg.contact_phone).replace(/[^+\d]/g, "")}`;
            buttons.push(
              <a key="call" href={tel} data-testid="event-cta-call"
                 style={{ padding: "14px 18px", borderRadius: 12,
                          background: "#FFD60A", color: "#1A1A30", textAlign: "center",
                          fontFamily: "Poppins", fontWeight: 800, fontSize: 15, textTransform: "lowercase",
                          textDecoration: "none", minHeight: 48, display: "block" }}>
                call {htg.contact_phone}
              </a>);
          }
          if (htg.contact_email) {
            buttons.push(
              <a key="email" href={`mailto:${htg.contact_email}`} data-testid="event-cta-email"
                 style={{ padding: "14px 18px", borderRadius: 12,
                          background: "transparent", color: "var(--ww-text)",
                          border: "1px solid var(--ww-line-strong)", textAlign: "center",
                          fontFamily: "Poppins", fontWeight: 700, fontSize: 14, textTransform: "lowercase",
                          textDecoration: "none", minHeight: 48, display: "block" }}>
                email {htg.contact_email}
              </a>);
          }
          return (
            <div data-testid="event-cta-contact-block"
                 style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 8 }}>
              <p className="lowercase" style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14, textAlign: "center", color: "var(--ww-text)" }}>
                {headline}
              </p>
              <div style={{ display: "grid", gridTemplateColumns: buttons.length > 1 ? "1fr 1fr" : "1fr", gap: 8 }}>
                {buttons}
              </div>
            </div>
          );
        }
        if (htg && htg.type === "walk_in" && !isPaid) {
          return (
            <p data-testid="event-cta-walkin" className="lowercase"
               style={{ gridColumn: "1 / -1", padding: "14px 18px", borderRadius: 12,
                        background: "var(--ww-surface)", color: "var(--ww-text)",
                        border: "1px solid var(--ww-line)", textAlign: "center",
                        fontFamily: "Poppins", fontWeight: 700, fontSize: 15, minHeight: 48 }}>
              just show up · no ticket needed
            </p>
          );
        }
        return (
          <a href={ctaTarget} target="_blank" rel="noreferrer"
             data-testid="event-cta-tickets"
             onClick={() => trackTicketClick({
               eventId,
               organiserHandle: organiserHandle || "",
               source: "event_page",
             })}
             style={{
               gridColumn: "1 / -1", padding: "14px 18px", borderRadius: 12,
               background: "#FFD60A", color: "#1A1A30", textAlign: "center",
               fontFamily: "Poppins", fontWeight: 800, fontSize: 15, textTransform: "lowercase",
               textDecoration: "none", minHeight: 48,
             }}>
            {parts.map((p, i) => CURRENCY_CODES.includes(p)
              ? <span key={i} className="ww-currency">{p}</span>
              : <React.Fragment key={i}>{p}</React.Fragment>)}
          </a>
        );
      })()}
    </div>
  );
}

function TicketHint({ htgType }) {
  // WW-158 fix 2 (Vania 13 Oct · D-4 QA) · the small line under the
  // CTA buttons depends on how to get in. For contact (Call / Email)
  // the ticket-page copy was wrong; for walk-in the whole line is
  // noise. Return null for walk-in so the pill speaks for itself.
  if (htgType === "walk_in") return null;
  const copy = htgType === "contact"
    ? "calls or emails the curator directly"
    : "opens the curator's ticket page";
  return (
    <p data-testid="event-cta-hint" className="mt-2"
       style={{ fontFamily: "Arial", fontSize: 11, color: META, textAlign: "center" }}>
      {copy}
    </p>
  );
}

function AboutSection({ ev }) {
  if (!ev.description) return null;
  return (
    <div className="mt-8">
      <h2 className="mb-2 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 20 }}>
        about
      </h2>
      <p style={{ fontFamily: "Arial", fontSize: 15, lineHeight: 1.55, color: "var(--ww-text)", whiteSpace: "pre-wrap" }}>
        {ev.description}
      </p>
    </div>
  );
}

/**
 * Wave 3 #7 · SlotsSection · lists every date/screening/run when the event has
 * more than one slot. Each row shows: label (optional), day + time, per-slot
 * price (or "included"), per-slot ticket link, and per-slot going count.
 * Sorted ascending by start_at. No CTAs at slot level yet — the top of page
 * still owns the overall "i'm going / interested" intent.
 */
function SlotsSection({ ev }) {
  if (!ev?.has_multiple_slots) return null;
  const slots = ev.slots_view || [];
  if (slots.length < 2) return null;
  const nowIso = new Date().toISOString();
  const cleanHandle = (ev.organiser_handle || "").replace(/^@+/, "");
  return (
    <section id="event-slots" className="mt-8" data-testid="event-slots">
      <h2 className="mb-3 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 20 }}>
        all dates
      </h2>
      <ul style={{ display: "grid", gap: 8, listStyle: "none", padding: 0, margin: 0 }}>
        {slots.map((s, i) => {
          const past = (s.end_at || s.start_at || "") < nowIso;
          const going = s.going_count || 0;
          const priceLabel = upperKES((s.price || "").trim());
          const priceParts = priceLabel.split(CURRENCY_SPLIT);
          return (
            <li key={s.slot_id || `slot-${i}`} data-testid={`event-slot-row-${i}`}
                style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                  border: `1px solid ${LINE}`, background: CARD_BG, borderRadius: 12,
                  opacity: past ? 0.55 : 1,
                }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {s.label && (
                  <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.2em",
                              fontSize: 10, color: META, marginBottom: 2 }}>
                    {s.label.toLowerCase()}
                  </p>
                )}
                <p style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14,
                            color: "var(--ww-text)", textTransform: "lowercase" }}>
                  {formatDateLine(s.start_at, s.end_at)}
                </p>
                <p style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 2 }}>
                  {priceLabel
                    ? priceParts.map((p, k) => CURRENCY_CODES.includes(p)
                        ? <span key={k} className="ww-currency">{p}</span>
                        : <React.Fragment key={k}>{p}</React.Fragment>)
                    : "price on ticket page"}
                  {going > 0 && <span style={{ marginLeft: 10 }}>· {going === 1 ? "1 going" : `${going} going`}</span>}
                </p>
              </div>
              {!past && s.ticket_link && (
                <a href={s.ticket_link} target="_blank" rel="noreferrer"
                   data-testid={`event-slot-link-${i}`}
                   onClick={() => trackTicketClick({
                     eventId: ev.id,
                     organiserHandle: cleanHandle,
                     source: "event_page",
                   })}
                   style={{
                     display: "inline-block", padding: "10px 14px", borderRadius: 999,
                     background: "#FFD60A", color: "#1A1A30", textDecoration: "none",
                     fontFamily: "Poppins", fontWeight: 700, fontSize: 12, textTransform: "lowercase",
                     whiteSpace: "nowrap",
                   }}>
                  get tickets
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function WhereBlock({ ev, venue }) {
  const address = venue?.address || venue?.name || ev?.venue_display || "";
  if (!address) return null;
  const mapsUrl = venueMapsUrl(venue || {}, ev?.venue_display || "");
  const staticMap = `https://staticmap.openstreetmap.de/staticmap.php?center=Nairobi,Kenya&zoom=12&size=600x260&maptype=mapnik&markers=Nairobi,Kenya,ol-marker`;
  return (
    <section className="mt-8" data-testid="event-where">
      <h2 className="mb-3 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 20 }}>
        where
      </h2>
      <div className="rounded-2xl overflow-hidden border" style={{ borderColor: LINE, background: CARD_BG }}>
        {/* WW-146.j · no Nairobi map for outside-Kenya items */}
        {!isSafari(ev) && (
        <a href={mapsUrl || undefined} target="_blank" rel="noreferrer" data-testid="event-where-map"
           style={{ display: "block", background: "#0f0f22" }}>
          <img src={staticMap} alt="" style={{ width: "100%", height: 180, objectFit: "cover", opacity: 0.9 }}
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </a>
        )}
        <div className="p-4">
          {venue?.name && (
            <p style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 15, textTransform: "lowercase" }}>
              {venue.name.toLowerCase()}
            </p>
          )}
          <p style={{ fontFamily: "Arial", fontSize: 13, color: META, marginTop: 2 }}>{address}</p>
          {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="event-where-directions"
             style={{ display: "inline-block", marginTop: 10, padding: "10px 16px", borderRadius: 999,
                      background: "#FFD60A", color: "#1A1A30",
                      fontFamily: "Poppins", fontWeight: 700, fontSize: 13, textDecoration: "none",
                      textTransform: "lowercase" }}>
            open in google maps
          </a>}
        </div>
      </div>
    </section>
  );
}

function WhoBlock({ ev, cleanHandle, orgMore }) {
  const [followState, setFollowState] = React.useState({ following: false, followers: 0 });
  const [orgState, setOrgState] = React.useState({ display_state: "", name: "" });
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (!cleanHandle) return;
    let alive = true;
    const c = axios.create({ baseURL: `${API}/api` });
    c.get(`/follow/counts?kind=organiser&key=${cleanHandle}`).then((r) => alive && setFollowState((s) => ({ ...s, followers: r.data.followers || 0 }))).catch(() => {});
    c.get(`/public/organisers/${encodeURIComponent(cleanHandle)}`).then((r) => {
      if (!alive) return;
      const o = r.data.organiser || {};
      setOrgState({ display_state: o.kibanda_display_state || "", name: o.name || "" });
    }).catch(() => {});
    const tok = localStorage.getItem("session_token");
    if (tok) {
      c.get(`/follow/status?kind=organiser&key=${cleanHandle}`, { headers: { Authorization: `Bearer ${tok}` } })
        .then((r) => alive && setFollowState((s) => ({ ...s, following: !!r.data.following }))).catch(() => {});
    }
    return () => { alive = false; };
  }, [cleanHandle]);
  if (!cleanHandle) return null;
  const isKibanda = ["trial", "active"].includes(orgState.display_state);
  const ctaLabel = isKibanda ? "pita kibanda" : "more events by this curator";
  const toggleFollow = async () => {
    const tok = localStorage.getItem("session_token");
    if (!tok) { window.location.assign(`/admin/login?next=/app/event/${ev.id}`); return; }
    setBusy(true);
    const c = axios.create({ baseURL: `${API}/api` });
    try {
      if (followState.following) {
        await c.delete(`/follow?kind=organiser&key=${cleanHandle}`, { headers: { Authorization: `Bearer ${tok}` } });
        setFollowState((s) => ({ following: false, followers: Math.max(0, s.followers - 1) }));
      } else {
        await c.post("/follow", { kind: "organiser", key: cleanHandle }, { headers: { Authorization: `Bearer ${tok}` } });
        setFollowState((s) => ({ following: true, followers: s.followers + 1 }));
      }
    } finally { setBusy(false); }
  };
  return (
    <section className="mt-8" data-testid="event-who">
      <h2 className="mb-3 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 20 }}>
        who's behind it
      </h2>
      <div className="rounded-2xl border p-4 flex flex-wrap items-center gap-3" data-testid="who-card" style={{ borderColor: LINE, background: CARD_BG }}>
        <Link to={`/app/organiser/${cleanHandle}`} data-testid="who-org-link"
              style={{ width: 52, height: 52, borderRadius: 999, background: "#FFD60A", color: "#1A1A30",
                       display: "grid", placeItems: "center", flexShrink: 0,
                       fontFamily: "Poppins", fontWeight: 800, fontSize: 20, textTransform: "uppercase",
                       textDecoration: "none" }}>
          {cleanHandle.slice(0, 1)}
        </Link>
        <div style={{ flex: "1 1 160px", minWidth: 0 }}>
          <Link to={`/app/organiser/${cleanHandle}`} style={{ textDecoration: "none", color: "inherit" }}>
            <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 15, textTransform: "lowercase", overflow: "hidden", overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }} data-testid="who-name">@{cleanHandle}</p>
          </Link>
          <p data-testid="who-counts" style={{ fontFamily: "Arial", fontSize: 12, color: META }}>
            {(orgMore?.length || 0) + 1} upcoming · {followState.followers} followers
          </p>
        </div>
        <div data-testid="who-actions" className="flex flex-wrap gap-2" style={{ flex: "0 1 auto", maxWidth: "100%" }}>
        {/* Wave 4 #11/#12 · CTA text depends on tier. Free listing: "more
            events by [name]". Kibanda: "pita kibanda". Follow only on kibanda. */}
        {isKibanda ? (
          <button data-testid="who-follow" onClick={toggleFollow} disabled={busy}
                  style={{ padding: "10px 16px", borderRadius: 999,
                           background: followState.following ? "transparent" : "var(--ww-text)",
                           color: followState.following ? "var(--ww-text)" : "var(--ww-ground)",
                           border: `1px solid ${followState.following ? "var(--ww-line-strong)" : "var(--ww-text)"}`,
                           fontFamily: "Poppins", fontWeight: 600, fontSize: 13, textTransform: "lowercase",
                           cursor: busy ? "wait" : "pointer", minHeight: 44 }}>
            {followState.following ? "following" : "follow"}
          </button>
        ) : null}
        <Link to={`/app/organiser/${cleanHandle}`} data-testid="who-more-events"
              style={{ padding: "10px 16px", borderRadius: 999,
                       background: isKibanda ? "#FFD60A" : "transparent",
                       color: isKibanda ? "#1A1A30" : "var(--ww-text)",
                       border: `1px solid ${isKibanda ? "#FFD60A" : "var(--ww-line-strong)"}`,
                       fontFamily: "Poppins", fontWeight: 600, fontSize: 13, textTransform: "lowercase",
                       textDecoration: "none", minHeight: 44, display: "inline-flex", alignItems: "center",
                       maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {ctaLabel}
        </Link>
        </div>
      </div>
    </section>
  );
}

function RelatedList({ title, rows, setViewer, empty }) {
  if (!rows || rows.length === 0) {
    if (!empty) return null;
    return (
      <section className="mt-8">
        <h2 className="mb-3 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 20 }}>{title}</h2>
        <p style={{ fontFamily: "Arial", color: META, fontSize: 13 }}>{empty}</p>
      </section>
    );
  }
  return (
    <section className="mt-8">
      <h2 className="mb-3 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 20 }}>{title}</h2>
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
        {rows.map((e) => <CompactCard key={e.id} event={e} onOpenPoster={setViewer} />)}
      </div>
    </section>
  );
}
